import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Grid, Chip, Tooltip, CircularProgress, Alert,
  FormControlLabel, Checkbox, Table, TableBody, TableCell, TableHead, TableRow,
  Divider, ToggleButtonGroup, ToggleButton, ListSubheader, Popover,
} from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Add from '@mui/icons-material/Add';
import Print from '@mui/icons-material/Print';
import Delete from '@mui/icons-material/Delete';
import Today from '@mui/icons-material/Today';
import {
  techniciansAPI, technicianAssignmentsAPI, projectsAPI, manufacturingAPI,
} from '../../services/api';

const WEEKDAYS = ['lun', 'mar', 'mie', 'jue', 'vie', 'sab', 'dom'];

const MONTH_NAMES = Array.from({ length: 12 }, (_, m) =>
  new Date(2000, m, 1).toLocaleDateString('es-ES', { month: 'long' }));

const ASSIGNMENT_TYPES = [
  { value: 'work', label: 'Trabajo', color: '#1565C0' },
  { value: 'travel', label: 'Desplazamiento', color: '#00838F' },
  { value: 'training', label: 'Formacion', color: '#6A1B9A' },
  { value: 'vacation', label: 'Vacaciones', color: '#854F0B' },
  { value: 'absence', label: 'Ausencia / baja', color: '#8D8D85' },
];

const PERIOD_TYPES = [
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'range', label: 'Rango libre' },
];

const NON_PRODUCTIVE = ['vacation', 'absence'];

// Estados con oferta aprobada o posteriores: son proyectos, no peticiones de oferta
const APPROVED_STATUSES = ['offer_approved', 'in_progress', 'commissioning', 'completed'];

const TYPE_STYLE = {
  vacation: { color: '#854F0B', bg: '#FAEEDA' },
  absence: { color: '#5F5E5A', bg: '#EEEDE7' },
  training: { color: '#6A1B9A', bg: '#F3E8F8' },
  travel: { color: '#00838F', bg: '#E0F4F6' },
};

// --- Utilidades de fecha (siempre en horario local, sin desfase UTC) ---
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromISO = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));
const isSameDay = (a, b) => toISO(a) === toISO(b);

const projectLabel = (p) => `${p.code || p.offer_code || p.id} - ${p.name}`;

const EMPTY_ASSIGNMENT = {
  technician_id: '', resource_name: '', project_id: '', manufacturing_order_id: '',
  task: '', assignment_type: 'work', period_type: 'day',
  start_date: '', end_date: '', hours_per_day: 8, include_weekends: false,
  location: '', notes: '',
};

export default function PlanningCalendar() {
  const today = new Date();
  const [refDate, setRefDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [technicians, setTechnicians] = useState([]);      // de alta en el mes visible
  const [allTechnicians, setAllTechnicians] = useState([]); // repertorio completo (selector)
  const [assignments, setAssignments] = useState([]);
  const [workload, setWorkload] = useState(null);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ASSIGNMENT);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Selector de mes/ano al pulsar sobre la fecha
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [pickerYear, setPickerYear] = useState(today.getFullYear());

  const openMonthPicker = (e) => {
    setPickerYear(refDate.getFullYear());
    setPickerAnchor(e.currentTarget);
  };

  const pickMonth = (monthIndex) => {
    setRefDate(new Date(pickerYear, monthIndex, 1));
    setPickerAnchor(null);
  };

  // Proyectos primero, despues las peticiones de oferta; dentro de cada grupo, el mas reciente arriba
  const projectGroups = useMemo(() => {
    const status = (p) => (p.status || '').toLowerCase();
    const byCode = (a, b) => String(b.code || b.offer_code || '')
      .localeCompare(String(a.code || a.offer_code || ''), 'es', { numeric: true });
    return [
      { label: 'Proyectos', items: projects.filter((p) => APPROVED_STATUSES.includes(status(p))).sort(byCode) },
      {
        label: 'Peticiones de oferta',
        items: projects
          .filter((p) => !APPROVED_STATUSES.includes(status(p)) && status(p) !== 'cancelled')
          .sort(byCode),
      },
      { label: 'Cancelados', items: projects.filter((p) => status(p) === 'cancelled').sort(byCode) },
    ].filter((g) => g.items.length > 0);
  }, [projects]);

  // Opciones agrupadas con cabecera por grupo (solo si hay mas de un grupo)
  const projectOptions = () => projectGroups.flatMap((g) => [
    ...(projectGroups.length > 1 ? [<ListSubheader key={`grp-${g.label}`}>{g.label}</ListSubheader>] : []),
    ...g.items.map((p) => <MenuItem key={p.id} value={p.id}>{projectLabel(p)}</MenuItem>),
  ]);

  // Rejilla: semanas completas de lunes a domingo que cubren el mes
  const gridDays = useMemo(() => {
    const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const last = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
    const start = mondayOf(first);
    const end = addDays(mondayOf(last), 6);
    const days = [];
    for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
    return days;
  }, [refDate]);

  const gridStart = toISO(gridDays[0]);
  const gridEnd = toISO(gridDays[gridDays.length - 1]);
  const monthStart = toISO(new Date(refDate.getFullYear(), refDate.getMonth(), 1));
  const monthEnd = toISO(new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0));

  const loadCalendar = useCallback(async () => {
    try {
      setLoading(true);
      const [calResp, wlResp] = await Promise.all([
        technicianAssignmentsAPI.calendar(gridStart, gridEnd, filterProject || undefined),
        technicianAssignmentsAPI.workload(monthStart, monthEnd),
      ]);
      setTechnicians(calResp.data.technicians);
      setAssignments(calResp.data.assignments);
      setWorkload(wlResp.data);
      setError(null);
    } catch (err) {
      console.error('Error loading calendar:', err);
      setError('Error al cargar el calendario de tecnicos.');
    } finally {
      setLoading(false);
    }
  }, [gridStart, gridEnd, monthStart, monthEnd, filterProject]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  useEffect(() => {
    projectsAPI.list()
      .then((r) => setProjects(r.data))
      .catch(() => setProjects([]));
    techniciansAPI.list()
      .then((r) => setAllTechnicians(r.data))
      .catch(() => setAllTechnicians([]));
  }, []);

  // Ordenes de fabricacion del proyecto seleccionado en el formulario
  useEffect(() => {
    if (!form.project_id) { setOrders([]); return; }
    let active = true;
    manufacturingAPI.list(form.project_id)
      .then((r) => { if (active) setOrders(r.data); })
      .catch(() => { if (active) setOrders([]); });
    return () => { active = false; };
  }, [form.project_id]);

  // El selector de orden de trabajo solo se muestra si el proyecto tiene ordenes
  const showOrders = !!form.project_id && (orders.length > 0 || !!form.manufacturing_order_id);

  // Indice: fecha ISO -> asignaciones que cubren ese dia
  const byDay = useMemo(() => {
    const map = {};
    assignments.forEach((a) => {
      if (!a.start_date || !a.end_date) return;
      const end = fromISO(a.end_date);
      for (let d = fromISO(a.start_date); d <= end; d = addDays(d, 1)) {
        const key = toISO(d);
        if (key < gridStart || key > gridEnd) continue;
        (map[key] = map[key] || []).push(a);
      }
    });
    return map;
  }, [assignments, gridStart, gridEnd]);

  const techActiveOn = (t, iso) =>
    (!t.start_date || t.start_date <= iso) && (!t.end_date || t.end_date >= iso);

  // Recursos puntuales (nombres libres) visibles en el mes, para la leyenda
  const adHocNames = useMemo(
    () => [...new Set(assignments.filter((a) => !a.technician_id).map((a) => a.display_name))],
    [assignments]
  );

  const monthLabel = refDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  // --- Dialogo ---
  const openNew = (technicianId, iso) => {
    setEditing(null);
    setForm({
      ...EMPTY_ASSIGNMENT,
      technician_id: technicianId || '',
      start_date: iso || monthStart,
      end_date: iso || monthStart,
      project_id: filterProject || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      technician_id: a.technician_id || '',
      resource_name: a.resource_name || '',
      project_id: a.project_id || '',
      manufacturing_order_id: a.manufacturing_order_id || '',
      task: a.task || '',
      assignment_type: a.assignment_type || 'work',
      period_type: a.period_type || 'range',
      start_date: a.start_date || '',
      end_date: a.end_date || '',
      hours_per_day: a.hours_per_day ?? 8,
      include_weekends: !!a.include_weekends,
      location: a.location || '',
      notes: a.notes || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'project_id') next.manufacturing_order_id = '';
      // Al cambiar el periodo o el inicio, recalcula el fin salvo en rango libre
      if (field === 'period_type' || field === 'start_date') {
        next.end_date = computeEnd(next.start_date, next.period_type, next.end_date);
      }
      return next;
    });
  };

  const computeEnd = (startISO, periodType, currentEnd) => {
    if (!startISO) return currentEnd;
    const start = fromISO(startISO);
    if (periodType === 'day') return startISO;
    if (periodType === 'week') return toISO(addDays(mondayOf(start), 6));
    if (periodType === 'month') return toISO(new Date(start.getFullYear(), start.getMonth() + 1, 0));
    return currentEnd || startISO;
  };

  const handleSave = async () => {
    if (!form.technician_id && !form.resource_name.trim()) {
      setFormError('Selecciona un tecnico del repertorio o escribe el nombre del tecnico/empresa.');
      return;
    }
    if (!form.start_date) {
      setFormError('La fecha de inicio es obligatoria.');
      return;
    }
    const payload = {
      ...form,
      technician_id: form.technician_id || null,
      resource_name: form.technician_id ? null : form.resource_name.trim(),
      project_id: form.project_id || null,
      manufacturing_order_id: form.manufacturing_order_id || null,
      hours_per_day: form.hours_per_day === '' ? null : parseFloat(form.hours_per_day),
      end_date: computeEnd(form.start_date, form.period_type, form.end_date),
    };
    try {
      setSaving(true);
      if (editing) {
        await technicianAssignmentsAPI.update(editing.id, payload);
      } else {
        await technicianAssignmentsAPI.create(payload);
      }
      setDialogOpen(false);
      await loadCalendar();
    } catch (err) {
      console.error('Error saving assignment:', err);
      setFormError(err.response?.data?.detail || 'Error al guardar la asignacion.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      setSaving(true);
      await technicianAssignmentsAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      setDialogOpen(false);
      await loadCalendar();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      setConfirmDelete(null);
      setFormError('Error al eliminar la asignacion.');
    } finally {
      setSaving(false);
    }
  };

  // --- Celda: una fila por recurso ---
  const renderResourceRow = (key, name, color, dayAssignments, onEmptyClick) => (
    <Box
      key={key}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 0.5,
        borderBottom: '1px dotted', borderColor: 'divider', py: '2px',
      }}
    >
      <Box sx={{ width: 5, height: 5, minWidth: 5, borderRadius: '50%', bgcolor: color, mt: '4px' }} />
      <Typography sx={{ fontSize: 8.5, color: 'text.disabled', minWidth: 30, lineHeight: 1.4 }} noWrap>
        {name}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {dayAssignments.length === 0 ? (
          <Box
            onClick={onEmptyClick}
            sx={{
              fontSize: 9.5, color: 'text.disabled', cursor: 'pointer', lineHeight: 1.4,
              '&:hover': { color: 'primary.main' },
            }}
          >
            +
          </Box>
        ) : dayAssignments.map((a) => {
          const style = TYPE_STYLE[a.assignment_type];
          const label = a.assignment_type === 'work'
            ? [a.project_code, a.task].filter(Boolean).join(' · ') || 'Asignado'
            : (ASSIGNMENT_TYPES.find((t) => t.value === a.assignment_type)?.label || a.task);
          return (
            <Tooltip
              key={a.id}
              title={[
                a.display_name,
                a.project_name && `Proyecto: ${a.project_code || ''} ${a.project_name}`,
                a.manufacturing_order_code && `OF: ${a.manufacturing_order_code}`,
                a.task,
                `${a.start_date} a ${a.end_date} · ${a.days} dias · ${a.hours} h`,
                a.location && `Lugar: ${a.location}`,
              ].filter(Boolean).join('\n')}
            >
              <Box
                onClick={() => openEdit(a)}
                sx={{
                  fontSize: 9.5, lineHeight: 1.4, cursor: 'pointer', wordBreak: 'break-word',
                  borderRadius: '3px', px: style ? '3px' : 0,
                  color: style?.color, bgcolor: style?.bg,
                  fontWeight: style ? 600 : 400,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {label}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );

  if (loading && !workload) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .cal-grid { break-inside: avoid; }
        }
      `}</style>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Barra de control */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <IconButton size="small" onClick={() => setRefDate(new Date(refDate.getFullYear(), refDate.getMonth() - 1, 1))}>
          <ChevronLeft />
        </IconButton>
        <Tooltip title="Elegir mes y ano">
          <Button
            onClick={openMonthPicker}
            endIcon={<ArrowDropDown />}
            sx={{
              minWidth: 200, justifyContent: 'space-between', textTransform: 'capitalize',
              fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', px: 1,
            }}
          >
            {monthLabel}
          </Button>
        </Tooltip>
        <IconButton size="small" onClick={() => setRefDate(new Date(refDate.getFullYear(), refDate.getMonth() + 1, 1))}>
          <ChevronRight />
        </IconButton>
        <Tooltip title="Mes actual">
          <IconButton size="small" onClick={() => setRefDate(new Date(today.getFullYear(), today.getMonth(), 1))}>
            <Today fontSize="small" />
          </IconButton>
        </Tooltip>

        <TextField
          select size="small" label="Filtrar por proyecto" value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)} sx={{ minWidth: 240, ml: 1 }}
        >
          <MenuItem value="">Todos los proyectos</MenuItem>
          {projectOptions()}
        </TextField>

        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<Add />} variant="contained" onClick={() => openNew(null, monthStart)}>
          Nueva asignacion
        </Button>
        <Button size="small" startIcon={<Print />} variant="outlined" onClick={() => window.print()}>
          Imprimir
        </Button>
      </Box>

      {/* Selector de mes y ano */}
      <Popover
        open={!!pickerAnchor} anchorEl={pickerAnchor} onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 280 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton size="small" onClick={() => setPickerYear((y) => y - 1)}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="subtitle1" fontWeight="bold">{pickerYear}</Typography>
            <IconButton size="small" onClick={() => setPickerYear((y) => y + 1)}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
            {MONTH_NAMES.map((name, m) => {
              const isSelected = pickerYear === refDate.getFullYear() && m === refDate.getMonth();
              const isCurrent = pickerYear === today.getFullYear() && m === today.getMonth();
              return (
                <Button
                  key={name} size="small" onClick={() => pickMonth(m)}
                  variant={isSelected ? 'contained' : isCurrent ? 'outlined' : 'text'}
                  sx={{ textTransform: 'capitalize', fontSize: 12, minWidth: 0 }}
                >
                  {name.slice(0, 3)}
                </Button>
              );
            })}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Button
            size="small" fullWidth startIcon={<Today fontSize="small" />}
            onClick={() => { setRefDate(new Date(today.getFullYear(), today.getMonth(), 1)); setPickerAnchor(null); }}
          >
            Ir al mes actual
          </Button>
        </Box>
      </Popover>

      {/* Leyenda */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
        {technicians.map((t) => (
          <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: t.color }} />
            <Typography variant="caption" color="textSecondary">
              {t.name}{t.role ? ` · ${t.role}` : ''}
            </Typography>
          </Box>
        ))}
        {adHocNames.map((n) => (
          <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#B4B2A9' }} />
            <Typography variant="caption" color="textSecondary">{n} · puntual</Typography>
          </Box>
        ))}
        {technicians.length === 0 && adHocNames.length === 0 && (
          <Typography variant="caption" color="textSecondary">
            No hay tecnicos de alta en este mes. Da de alta el repertorio en la pestana Tecnicos.
          </Typography>
        )}
      </Box>

      {/* Cabecera de dias de la semana */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px', mb: '4px' }}>
        {WEEKDAYS.map((d, i) => (
          <Typography
            key={d} align="center"
            sx={{ fontSize: 11, fontWeight: 600, color: i > 4 ? 'text.disabled' : 'primary.main' }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      {/* Rejilla del mes */}
      <Box className="cal-grid" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px' }}>
        {gridDays.map((day) => {
          const iso = toISO(day);
          const inMonth = day.getMonth() === refDate.getMonth();
          const isToday = isSameDay(day, today);
          const dayAssignments = byDay[iso] || [];
          const activeTechs = technicians.filter((t) => techActiveOn(t, iso));
          const adHoc = dayAssignments.filter((a) => !a.technician_id);
          const adHocGroups = [...new Set(adHoc.map((a) => a.display_name))];

          return (
            <Paper
              key={iso} variant="outlined"
              sx={{
                minHeight: 128, p: 0.75, borderRadius: 2,
                opacity: inMonth ? 1 : 0.45,
                bgcolor: isToday ? '#FCEBEB' : 'background.paper',
                borderColor: isToday ? '#F0C4C4' : 'divider',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? 'primary.main' : 'text.primary' }}>
                  {day.getDate()}
                </Typography>
                <Tooltip title="Anadir tecnico o empresa puntual">
                  <IconButton
                    className="no-print" size="small" sx={{ p: 0 }}
                    onClick={() => openNew(null, iso)}
                  >
                    <Add sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {activeTechs.map((t) =>
                renderResourceRow(
                  `t${t.id}`,
                  t.name.split(' ')[0],
                  t.color,
                  dayAssignments.filter((a) => a.technician_id === t.id),
                  () => openNew(t.id, iso)
                )
              )}

              {adHocGroups.map((name) =>
                renderResourceRow(
                  `n${name}`,
                  name.split(' ')[0],
                  '#B4B2A9',
                  adHoc.filter((a) => a.display_name === name),
                  () => openNew(null, iso)
                )
              )}
            </Paper>
          );
        })}
      </Box>

      {/* Resumen de cargas del mes */}
      {workload && (
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Carga por recurso · {monthLabel}
              </Typography>
              {workload.by_resource.length === 0 ? (
                <Typography variant="body2" color="textSecondary">Sin asignaciones este mes.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Recurso</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Dias</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Horas</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Proyectos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workload.by_resource.map((r) => (
                      <TableRow key={r.technician_id || r.name}>
                        <TableCell>
                          {r.name}
                          {r.absence_hours > 0 && (
                            <Chip label={`${r.absence_hours} h no productivas`} size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{r.days}</TableCell>
                        <TableCell align="right"><strong>{r.hours}</strong></TableCell>
                        <TableCell>{r.projects.join(', ') || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Recursos por proyecto · {monthLabel} ({workload.total_hours} h)
              </Typography>
              {workload.by_project.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Sin horas imputadas a proyectos este mes.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Proyecto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>OF</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Dias</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Horas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workload.by_project.map((p) => (
                      <TableRow key={p.project_id}>
                        <TableCell>
                          <strong>{p.project_code || '-'}</strong> {p.project_name}
                          <Typography variant="caption" color="textSecondary" display="block">
                            {p.resources.join(', ')}
                          </Typography>
                        </TableCell>
                        <TableCell>{p.orders.join(', ') || '-'}</TableCell>
                        <TableCell align="right">{p.days}</TableCell>
                        <TableCell align="right"><strong>{p.hours}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Dialogo de asignacion */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar asignacion' : 'Nueva asignacion'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Tecnico del repertorio" value={form.technician_id}
                onChange={setField('technician_id')}
                helperText="Deja vacio para un tecnico o empresa puntual"
              >
                <MenuItem value="">-- Puntual (nombre libre) --</MenuItem>
                {allTechnicians.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}{t.role ? ` (${t.role})` : ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Tecnico / empresa puntual" value={form.resource_name}
                onChange={setField('resource_name')} disabled={!!form.technician_id}
                placeholder="Ej: Montajes Garcia SL"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Tipo" value={form.assignment_type} onChange={setField('assignment_type')}>
                {ASSIGNMENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ToggleButtonGroup
                exclusive size="small" value={form.period_type} fullWidth
                onChange={(e, v) => v && setField('period_type')({ target: { value: v } })}
              >
                {PERIOD_TYPES.map((p) => (
                  <ToggleButton key={p.value} value={p.value} sx={{ fontSize: 11 }}>{p.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="Desde" value={form.start_date}
                onChange={setField('start_date')} InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="Hasta"
                value={computeEnd(form.start_date, form.period_type, form.end_date)}
                onChange={setField('end_date')} InputLabelProps={{ shrink: true }}
                disabled={form.period_type !== 'range'}
                helperText={form.period_type !== 'range' ? 'Calculado segun el periodo' : ''}
              />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12} sm={showOrders ? 6 : 12}>
              <TextField select fullWidth label="Proyecto" value={form.project_id} onChange={setField('project_id')}>
                <MenuItem value="">Sin proyecto</MenuItem>
                {projectOptions()}
              </TextField>
            </Grid>
            {showOrders && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Orden de trabajo (OF)" value={form.manufacturing_order_id}
                  onChange={setField('manufacturing_order_id')}
                >
                  <MenuItem value="">Sin orden</MenuItem>
                  {orders.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.code || o.id}{o.name ? ` - ${o.name}` : ''}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField fullWidth label="Tarea" value={form.task} onChange={setField('task')}
                placeholder="Ej: Montaje mecanico de tanques" />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Horas / dia" value={form.hours_per_day}
                onChange={setField('hours_per_day')} inputProps={{ step: 0.5, min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Lugar" value={form.location} onChange={setField('location')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={form.include_weekends} onChange={setField('include_weekends')} />}
                label={<Typography variant="body2">Contar fines de semana</Typography>}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notas" value={form.notes} onChange={setField('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editing && (
            <Button color="error" startIcon={<Delete />} onClick={() => setConfirmDelete(editing)} disabled={saving}>
              Eliminar
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmacion de borrado de asignacion */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar asignacion</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Se eliminara esta asignacion del calendario:</Typography>
          {confirmDelete && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2"><strong>{confirmDelete.display_name}</strong></Typography>
              <Typography variant="body2" color="textSecondary">
                {confirmDelete.start_date} a {confirmDelete.end_date} · {confirmDelete.days} dias · {confirmDelete.hours} h
              </Typography>
              {(confirmDelete.project_code || confirmDelete.project_name) && (
                <Typography variant="body2" color="textSecondary">
                  {confirmDelete.project_code} {confirmDelete.project_name}
                  {confirmDelete.manufacturing_order_code ? ` · OF ${confirmDelete.manufacturing_order_code}` : ''}
                </Typography>
              )}
              {confirmDelete.task && (
                <Typography variant="body2" color="textSecondary">{confirmDelete.task}</Typography>
              )}
            </Paper>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5 }}>
            Las horas dejaran de imputarse al proyecto. Esta accion no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={saving}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
            {saving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
